import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'

import { isAuthenticated } from '@/libs/auth'
import { landingEntryName } from '@/libs/navigation'
import { mayOpenGate } from '@/libs/navPermission'
import { useAppStore } from '@/stores/app'

/**
 * Slovenian addresses, like the operations console and unlike the reception portal: this
 * is a Slovenian back office and the people who open it read them. (The HOSTNAME is
 * English — `uprava.` is the one exception the root CLAUDE.md's rule leaves for a portal
 * nobody outside the company ever opens.)
 *
 * Twelve screens after G4 and G9 — seven of G0 and G1, „Kanali“ (WP-G9), then Milo, the
 * reports, the teki of one report and one tek. Every one of them declares what it needs:
 *
 *   - `meta.requiresAuth` — a session;
 *   - `meta.permission`   — the group right the screen is drawn from, and the one the
 *                           sidebar filters on;
 *   - `meta.allOf`        — every right the screen's FIRST reads need, where that is more
 *                           than one. Three screens carry it: their own right opens the
 *                           board and `group:read` opens the register of companies the
 *                           board is assembled per. It is an AND and never an OR — the
 *                           reception portal's own vocabulary, so that the two consoles
 *                           are read the same way (`pms-web/src/router/index.ts`).
 *
 * What this file is NOT is the menu. The order of the screens, and which of them are
 * screens at all, is `libs/navigation.ts` — one ordered list the sidebar draws from and
 * the landing walks. This table is the ROUTER: it also holds the detail routes, in
 * whatever order they were written, and an account may never be SENT to one of those.
 *
 * A screen declares what its own load NEEDS and never less. That sentence is the whole of
 * what `HTTP_API/ConsolePermissions_test.go` in the service holds this file to: it walks
 * the router of the service, reads the routes below out of THIS file, and fails when a
 * screen is offered on a right the service would refuse its first read on. It is written
 * down here because it was learned the expensive way — „Pregled“ declared `group:read`
 * and its own read wanted `group:report`, so every account but „Skrbnik“ signed in, was
 * shown the board, and got a red box (docs/PRAVICE_IN_OBSEG.md §5).
 *
 * `group:read` on all but „Ljudje“ (`group:hr`): seeing who owns what, what the group did
 * yesterday, and whether it is still selling, is the lowest right this console has. What
 * `group:manage` buys is the buttons — the companies, the tree, the report currency and
 * „Obnovi posnetke“ — and what
 * `group:finance` buys is the monthly exchange rates; both are gated on the screens
 * themselves, because a person who may look at the tree still sees the tree. „Ljudje“ is
 * the exception and declares `group:hr`: a register of who works where under which
 * contract is a different circle of trust from the organisation chart (§8).
 *
 * None carries a MODULE, still. The G0 screens are reachable without the `group` licence
 * for the reason recorded in the project CLAUDE.md, and the G1 screens are not gated
 * either — not because the flash is not the licence, but because there is no tenant-wide
 * module read to gate it on: `pms-web`'s `hasModule` resolves the licence PER PROPERTY,
 * and this console never stands on one. Inventing a second way to ask would be inventing
 * a second answer. The gate belongs with the endpoint that answers it for a group, and
 * the refusal in the meantime is the service's own — a screen whose read is refused
 * prints the sentence it was refused with.
 */
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}

        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            // A STATIC redirect and not `landingRouteName()`, deliberately.
            //
            // A `redirect` function runs while the route is being resolved, which on a cold
            // load is before `ensureUserData` has answered — and an unread account is not an
            // account with no rights. It would send everybody who reloaded to whatever the
            // first permission-free screen happened to be. So the root names one screen, and
            // the guard below — which awaits the account — moves anybody who may not open it
            // to the one they may. One hop more, and the hop is where the answer is known.
            path:     '/',
            redirect: '/pregled',
        },
        {
            // The presentation server's one-click sign-in (Logic/Demo.go, DemoSignIn.vue).
            path:      '/demo/:role',
            name:      'demo-sign-in',
            component: () => import('@/views/DemoSignIn.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/prijava',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // „Pregled“ — the consolidated flash (docs/UPRAVA.md §17, WP-G1).
            //
            // `group:report` and NOT `group:read`, since 2026-09-19. It declared the
            // lower right until then, and the screen it opened could not be drawn: the
            // read this board IS — `GET /group/kpi/daily` — has always been guarded by
            // `group:report`, which only „Skrbnik“ carries. An ordinary account was let
            // into the console, landed here because this is the root's redirect, and met
            // a red box on the first paint. The right a screen declares has to be the
            // right its own read demands; anything lower is a menu entry promising a
            // screen the service will refuse.
            //
            // `GET /group/live/today` is the other read and is NOT what this declares:
            // it feeds one tile, a house that does not answer costs that tile its number,
            // and the screen states the failure in place rather than failing.
            path:      '/pregled',
            name:      'overview',
            component: () => import('@/views/overview/Overview.vue'),
            meta:      { requiresAuth: true, permission: 'group:report' },
        },
        {
            path:      '/opozorila',
            name:      'alerts',
            component: () => import('@/views/alert/Alerts.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            // Whether the group is still SELLING (docs/UPRAVA.md §30, WP-G9). `group:read`
            // like the rest: there is no money on it, and the service's own door is an OR
            // of `group:read` and `group:sales`, so the lower of the two is what a menu
            // entry may be drawn from without promising a screen that refuses.
            path:      '/kanali',
            name:      'channels',
            component: () => import('@/views/channel/Channels.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            // „Pickup“ — what is on the books, month by month (docs/UPRAVA.md §18).
            //
            // `group:report` for „Pregled“'s own reason, found in the same sweep: its one
            // read on mount is `GET /group/kpi/pickup`, which the service guards with
            // `group:report`. Declaring `group:read` offered the entry to every account in
            // the console and answered 403 on the first paint.
            path:      '/pickup',
            name:      'pickup',
            component: () => import('@/views/pickup/Pickup.vue'),
            meta:      { requiresAuth: true, permission: 'group:report' },
        },
        {
            // The group's corporate agreements, and what the firms behind them brought
            // (docs/UPRAVA.md §36, WP-G6). After „Pickup“ because that is the order the
            // question is asked in: what is on the books, and then with whom it was agreed.
            //
            // `group:sales` and not the OR the service's read door takes (`group:sales` OR
            // `group:read`), because a route's `meta.permission` is ONE key — `mayOpen`
            // asks `appStore.can(permission)` and the menu is drawn from the same field.
            // The narrower of the two is the safe one to declare: an entry leading to a 403
            // is worse than no entry, and a sales director, who is exactly who this screen
            // is for, holds `group:sales`.
            //
            // What it costs is stated rather than hidden, exactly as „Zaključek“ states its
            // own: an account holding ONLY `group:read` may read the register through the
            // API and is not offered the screen. When this router grows a permission LIST,
            // this is the second entry that wants one.
            path:      '/pogodbe',
            name:      'contracts',
            component: () => import('@/views/contract/Contracts.vue'),
            meta:      { requiresAuth: true, permission: 'group:sales' },
        },
        {
            // The central reservation service (docs/UPRAVA.md §37, WP-G7), and the first
            // screen of this console that WRITES into a hotel.
            //
            // `group:sales` and not `group:read`, although the service's read doors are an
            // OR of the two. A route's `meta.permission` is ONE key — `mayOpen` asks
            // `appStore.can(permission)` and the menu is drawn from the same field — and
            // here the NARROWER one is the honest declaration: the screen's whole purpose
            // is the two writes, and offering it to somebody who may look at the grid and
            // is refused at the button would be worse than not offering it.
            //
            // What it costs is stated rather than hidden: an account holding only
            // `group:read` may read the availability grid and the guest profile through the
            // API and is not offered the screen. This is „Zaključek“'s own note one screen
            // along, and when this router grows a permission LIST both entries want one.
            path:      '/rezervacije',
            name:      'reservations',
            component: () => import('@/views/reservation/Reservations.vue'),
            meta:      { requiresAuth: true, permission: 'group:sales' },
        },
        {
            // What the group's advertising brought back (docs/UPRAVA.md §43, WP-G21).
            // After „Rezervacije“ because that is the order the question is asked in:
            // what the group is selling, and then what the selling cost to produce.
            //
            // `group:marketing` and not the OR the service's read door takes
            // (`group:marketing` OR `group:report`), for „Pogodbe“'s own reason: a
            // route's `meta.permission` is ONE key — `mayOpen` asks
            // `appStore.can(permission)` and the menu is drawn from the same field — and
            // the person this screen is built for is the one who decides what next
            // quarter's campaigns are.
            //
            // What it costs is stated rather than hidden, as the other two entries with
            // this note state theirs: an account holding only `group:report` may read the
            // board through the API and is not offered the screen. This is the THIRD
            // entry that wants a permission LIST when the router grows one.
            path:      '/roi',
            name:      'marketing-roi',
            component: () => import('@/views/roi/MarketingRoi.vue'),
            meta:      { requiresAuth: true, permission: 'group:marketing' },
        },
        {
            // The congress that sleeps in several houses at once (docs/UPRAVA.md §40,
            // WP-G8), straight after „Rezervacije“ because it is the same errand one size
            // up: that screen sells one room in one hotel, and this one signs a contract
            // over four of them.
            //
            // `group:sales` for „Rezervacije“'s own reason, stated once more because it
            // costs the same thing: a route's `meta.permission` is ONE key, the service's
            // read doors are an OR of `group:sales` and `group:read`, and the NARROWER one
            // is the honest declaration — the screen's whole purpose is the writes, and
            // offering it to somebody who may read the board and is refused at every button
            // is worse than not offering it. An account holding only `group:read` may read
            // the register through the API and is not offered the screen.
            path:      '/skupine',
            name:      'master-groups',
            component: () => import('@/views/master/MasterGroups.vue'),
            meta:      { requiresAuth: true, permission: 'group:sales' },
        },
        {
            // ONE congress, and its own address rather than a dialog over the list: it is
            // what somebody is sent in an e-mail about a hotel that did not take it, and a
            // link that opens a screen with a dialog on it is a link that opens the wrong
            // thing when it is pasted twice. „Poročila“ makes the same choice one screen
            // along.
            path:      '/skupine/:id',
            name:      'master-group',
            component: () => import('@/views/master/MasterGroup.vue'),
            meta:      { requiresAuth: true, permission: 'group:sales' },
        },
        {
            // Skupinske kampanje in predloge znamke (docs/UPRAVA.md §41, WP-G20). After
            // „Rezervacije“ and before „Proračun“, which is the order the work is done in:
            // the group sells a room, and this is where it goes and asks for the next one.
            //
            // `group:marketing`, and it is NARROWER than „Ceniki“'s `group:sales` on
            // purpose: these screens carry an audience — masked guest addresses, counts of
            // who unsubscribed, and the suppression list itself — and §8 made marketing its
            // own circle of trust precisely so that „sees the group“ does not imply „sees
            // the mailing list“. The filter in the sidebar takes the entry out for an
            // account that may not open it rather than offering a screen that answers 403.
            path:      '/kampanje',
            name:      'campaigns',
            component: () => import('@/views/campaign/Campaigns.vue'),
            meta:      { requiresAuth: true, permission: 'group:marketing' },
        },
        {
            // The plan, and what happened to it (docs/UPRAVA.md §31, WP-G2). `group:report`
            // and not `group:read`: §8 made seven rights so that the person who draws the
            // organisation chart does not thereby read what every hotel is expected to
            // take. What the WRITE needs is more again — `group:finance` or `group:manage`
            // — and that is gated on the screen itself, because somebody who may read the
            // board still reads the board.
            path:      '/proracun',
            name:      'budgets',
            component: () => import('@/views/budget/Budgets.vue'),
            meta:      { requiresAuth: true, permission: 'group:report' },
        },
        {
            // Izkaz skupine — light (docs/UPRAVA.md §44, WP-G15). Straight after
            // „Proračun“, because it is the same errand one step on: that screen says what
            // the group SAID it would take, and this one says what it kept of what it did.
            //
            // The service's doors are an OR of `group:report` and `group:finance`, and the
            // route declares the BROADER of the two: unlike „Rezervacije“ one entry above,
            // this screen writes nothing at all, so there is no button behind it for a
            // controller to be refused at. An account holding only `group:finance` reaches
            // it through the API and is not offered the menu entry — the cost of
            // `meta.permission` being one key, and the reason this router wants a
            // permission LIST the day a third screen needs one.
            path:      '/izkaz',
            name:      'pnl',
            component: () => import('@/views/pnl/Pnl.vue'),
            meta:      { requiresAuth: true, permission: 'group:report' },
        },
        {
            // Centralne predloge cenikov (docs/UPRAVA.md §35, WP-G5). `group:sales` and
            // not `group:read`: deciding what forty hotels charge is central sales'
            // authority, which is one of §8's circles of trust and not a board anybody
            // may read — this screen is where a price is DECIDED and then written into
            // every house, so the right it declares is the right the press needs.
            //
            // The guard would refuse it anyway — this is what keeps the menu from
            // offering a door that answers 403.
            path:      '/ceniki',
            name:      'rate-templates',
            component: () => import('@/views/rate/RateTemplates.vue'),
            meta:      { requiresAuth: true, permission: 'group:sales' },
        },
        {
            // The one screen in this console whose right is NOT `group:read`, and
            // deliberately: what a group buys, from whom, against which agreement and what
            // it has not settled is central purchasing's (docs/UPRAVA.md §8, §28). The
            // guard would refuse it anyway — this is what keeps the menu from offering a
            // door that answers 403.
            path:      '/nabava',
            name:      'materials',
            component: () => import('@/views/materials/Materials.vue'),
            meta:      { requiresAuth: true, permission: 'group:finance' },
        },
        {
            // Knjigovodstvo po družbi (docs/UPRAVA.md §34, WP-G14), and the second screen
            // of this console behind `group:finance`. It sits beside „Nabava“ for the
            // reason it carries that right: §8's four shared services are four circles of
            // trust, and handing a company's purchases to its book-keeper is central
            // finance's act rather than a board anybody may read.
            //
            // The guard would refuse it anyway — this is what keeps the menu from
            // offering a door that answers 403.
            // `allOf` and not one key, since 2026-09-19. The screen's FIRST read is
            // `GET /group/legal-entities` — it fills the company picker every other read
            // hangs off, and `loadAll` stops when it fails — and the service guards that
            // register with `group:read` alone. An account holding only `group:finance`
            // was therefore offered a screen whose first call answered 403.
            //
            // Declared rather than widened on the service, which is the safe direction:
            // whether the group's register of companies should open on ANY group right is
            // a question for the owner (docs/PRAVICE_IN_OBSEG.md §6), and a console must
            // not answer it by itself.
            path:      '/knjigovodstvo',
            name:      'accounting',
            component: () => import('@/views/accounting/Accounting.vue'),
            meta:      { requiresAuth: true, permission: 'group:finance', allOf: ['group:finance', 'group:read'] },
        },
        {
            // Medpodjetniški račun iz prenosa zaloge (docs/UPRAVA.md §38, WP-G12), and the
            // third screen of this console behind `group:finance`. After „Knjigovodstvo“
            // because that is the order the work is done in: the purchases are handed to
            // the book-keeper, and what one company of the group sold another is the
            // document that has to exist before they can be.
            //
            // The guard would refuse it anyway — this is what keeps the menu from offering
            // a door that answers 403.
            // `allOf` for „Knjigovodstvo“'s own reason one screen up: `onMounted` reads
            // `GET /group/legal-entities` before anything else, and that register is
            // guarded by `group:read`.
            path:      '/medpodjetnisko',
            name:      'intercompany',
            component: () => import('@/views/intercompany/Intercompany.vue'),
            meta:      { requiresAuth: true, permission: 'group:finance', allOf: ['group:finance', 'group:read'] },
        },
        {
            path:      '/druzbe',
            name:      'entities',
            component: () => import('@/views/entity/LegalEntities.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            path:      '/struktura',
            name:      'structure',
            component: () => import('@/views/structure/Structure.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            path:      '/tecaji',
            name:      'fx',
            component: () => import('@/views/fx/FxRates.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            // The group's own monthly close (docs/UPRAVA.md §32, WP-G13). After „Tečaji“,
            // which is the order the work is done in: the rate is filed and then the month
            // is declared finished.
            //
            // `group:finance` and not the OR the service's read door takes (`group:report`
            // OR `group:finance`), because a route's `meta.permission` is ONE key —
            // `mayOpen` asks `appStore.can(permission)` and the menu is drawn from the same
            // field. The narrower of the two is the safe one to declare: an entry leading
            // to a 403 is worse than no entry, and a controller, who is exactly who this
            // screen is for, holds `group:finance`.
            //
            // What it costs is stated rather than hidden: an account holding ONLY
            // `group:report` may read the sheet through the API and is not offered the
            // screen. When this router grows a permission LIST, this is the first entry
            // that wants one.
            path:      '/zakljucek',
            name:      'month-close',
            component: () => import('@/views/close/MonthClose.vue'),
            meta:      { requiresAuth: true, permission: 'group:finance' },
        },
        {
            // The register of people (docs/UPRAVA.md §29, WP-G16), and the first screen
            // of this console that is NOT `group:read`. §8 made seven rights so that the
            // person who draws the organisation chart does not thereby read who works
            // under which contract in every hotel; the menu therefore hides it for an
            // account without `group:hr`, and the service refuses it anyway.
            path:      '/ljudje',
            name:      'people',
            component: () => import('@/views/people/People.vue'),
            meta:      { requiresAuth: true, permission: 'group:hr' },
        },
        {
            // The company's payroll file (docs/UPRAVA.md §33, WP-G18), and the second
            // screen of this console behind `group:hr`. It is beside „Ljudje“ rather than
            // beside the money for the reason §8 separates the two: personnel is a circle
            // of trust of its own, and what a person is paid is further inside it than
            // what a hotel earned.
            // `allOf` for „Knjigovodstvo“'s own reason: `loadData` reads
            // `GET /group/legal-entities` first — the file is assembled PER COMPANY, so
            // there is no sheet without the register — and that route is guarded by
            // `group:read`, which `group:hr` does not imply.
            path:      '/place',
            name:      'payroll',
            component: () => import('@/views/payroll/Payroll.vue'),
            meta:      { requiresAuth: true, permission: 'group:hr', allOf: ['group:hr', 'group:read'] },
        },
        {
            // Posoje osebja med hišami (docs/UPRAVA.md §39, WP-G17), and the third screen
            // of this console behind `group:hr`. Beside „Ljudje“ and „Plače“ rather than
            // beside the money, for §8's reason: who works where and who is lent to whom
            // is one circle of trust, and a person's week in another hotel is inside it.
            path:      '/posoje',
            name:      'loans',
            component: () => import('@/views/loan/Loans.vue'),
            meta:      { requiresAuth: true, permission: 'group:hr' },
        },
        {
            // Kadrovski kazalniki (docs/UPRAVA.md §42, WP-G19), and the fourth screen of
            // this console behind `group:hr`. After „Posoje“ because that is the order
            // the work is done in: who works here, what leaves for the wage run, who is
            // standing in another hotel — and then what all of it costs per sold night.
            //
            // `group:hr` and not the OR the service's read door takes (`group:hr` OR
            // `group:report`), because a route's `meta.permission` is ONE key: `mayOpen`
            // asks `appStore.can(permission)` and the menu is drawn from the same field.
            // The narrower of the two is the honest declaration here, because the screen
            // sits in the personnel section and a head of personnel is who opens it.
            //
            // What it costs is stated rather than hidden, exactly as „Zaključek“ and
            // „Pogodbe“ state their own: an account holding ONLY `group:report` may read
            // this board through the API and is not offered the screen. When this router
            // grows a permission LIST, this is the fourth entry that wants one.
            path:      '/kadri',
            name:      'staff',
            component: () => import('@/views/staff/Staff.vue'),
            meta:      { requiresAuth: true, permission: 'group:hr' },
        },
        {
            path:      '/zapisi',
            name:      'operations',
            component: () => import('@/views/operation/Operations.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            path:      '/milo',
            name:      'milo',
            component: () => import('@/views/milo/Milo.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            path:      '/porocila',
            name:      'reports',
            component: () => import('@/views/report/Reports.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            path:      '/porocila/:id',
            name:      'report-runs',
            component: () => import('@/views/report/ReportRuns.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        // The tek is its own address and not a dialog over the list, deliberately: it
        // is what somebody is sent in an e-mail, and a link that opens a screen with a
        // dialog on it is a link that opens the wrong thing when it is pasted twice.
        {
            path:      '/porocila/:id/teki/:runId',
            name:      'report-run',
            component: () => import('@/views/report/ReportRun.vue'),
            meta:      { requiresAuth: true, permission: 'group:read' },
        },
        {
            // Where an account with no group right at all lands, and the reason it is a
            // SCREEN rather than nothing.
            //
            // Until 2026-09-19 such an account stayed on the screen it had asked for, and
            // what it saw was the console's own furniture with a red box in the middle of
            // it — the service's refusal, printed by `AppLoadError`, which is the right
            // answer for a read that failed and the wrong one for an account that was
            // never meant to be here. „Napaka pri branju podatkov“ says a thing broke; it
            // does not say „this account holds no right this console is built on, ask
            // whoever set it up“.
            //
            // It declares NO permission, deliberately: it is the one screen everybody may
            // open, and it must never be what the landing search finds. What keeps it out
            // is that it is not in the navigation manifest (`libs/navigation.ts`), which
            // is the list the search walks — it is named in that file's `NOT_IN_MENU`
            // with this reason.
            //
            // It used to be held off by `meta.landing: false`, and that flag is gone: the
            // search no longer reads the router's route list at all, so the flag guarded
            // nothing and was a second way to say what „not in the menu“ already says.
            path:      '/brez-dostopa',
            name:      'no-access',
            component: () => import('@/views/NoAccess.vue'),
            meta:      { requiresAuth: true },
        },
    ],
})

/** What a route declares it needs — one key, and the full list where its load needs more. */
type RouteGate = { meta: { permission?: unknown, anyOf?: unknown, allOf?: unknown } }

/** What this account holds, asked the way the pure rule wants it. */
const accountHolds = (key: string): boolean => useAppStore().can(key)

/**
 * Whether this account may open a route, by what the route itself declares.
 *
 * The RULE is `libs/navPermission.ts` and imports nothing; this is the two lines that
 * hand it the route's `meta` and the store. The guard reads `meta` rather than the
 * manifest deliberately: a typed address reaches the guard without passing a menu, and
 * the detail routes — which are not in the manifest at all — are guarded here and nowhere
 * else.
 */
export const mayOpen = (route: RouteLocationNormalized | RouteGate): boolean =>
    mayOpenGate({
        permission: route.meta.permission as string | undefined,
        anyOf:      route.meta.anyOf as string[] | undefined,
        allOf:      route.meta.allOf as string[] | undefined,
    }, accountHolds)

/** The address a route name is registered at, or nothing where no route answers to it. */
const routePath = (name: string): string | undefined =>
    router.getRoutes().find(route => route.name === name)?.path

/**
 * Where a signed-in account belongs when it has not asked for anywhere in particular.
 *
 * It walks the navigation manifest IN MENU ORDER and answers the first entry this account
 * may open — the screen their morning starts with — or „Brez dostopa“, a sentence, where
 * it may open none.
 *
 * It used to walk `router.getRoutes()` instead, and that is the defect this whole file
 * pair exists to close: that list is not in menu order and holds the DETAIL routes, so an
 * account with one right was redirected onto `/porocila/:id/teki/:runId` or `/skupine/:id`
 * and stranded on an address vue-router could not build. The manifest holds screens only;
 * `isLandablePath` inside `landingEntryName` is the second lock, and it is fed the REAL
 * router's paths by `routePath` above so that a route which moved is still answered about
 * honestly.
 *
 * Exported because `tools/landing.test.mjs` calls THIS function — the one the console
 * runs — over all 128 subsets of the seven group rights, rather than a copy of it.
 */
export const landingRouteName = (): string =>
    landingEntryName(accountHolds, routePath) || 'no-access'

router.beforeEach(async (to, from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()

    if (loggedIn && to.name === 'login') {
        // The account first, then the landing: asking what somebody may open while the
        // answer is still in flight sends every sign-in to the same screen. This is the
        // ONE place the landing is decided — it used to name „Pregled“ here and let the
        // fallback below sort it out, which worked only while „Pregled“ was the screen
        // everybody could open.
        await appStore.ensureUserData()

        return next({ name: landingRouteName() })
    }

    if (!to.meta.requiresAuth) return next()

    if (!loggedIn) {
        appStore.setLogout()

        return next({ name: 'login' })
    }

    // The account BEFORE the decision below: a guard that asks what somebody may do
    // while the answer is still in flight sends them to the sign-in screen, once, on
    // every reload.
    await appStore.ensureUserData()

    if (!mayOpen(to)) {
        const fallback = landingRouteName()

        // A guard that redirects to a route it would refuse again is a loop, so the
        // fallback is only taken when it is a DIFFERENT screen. Where the account may
        // open nothing at all, `landingRouteName` answers „Brez dostopa“, which is a
        // sentence saying so — and not the board it asked for with the service's refusal
        // painted over it, which is what stood here until 2026-09-19.
        if (fallback !== to.name) return next({ name: fallback })
    }

    next()
})

export default router
